import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Button,
    InputBase,
    Avatar,
    styled,
    alpha,
} from '@mui/material';
import {
    Search as SearchIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    History as HistoryIcon,
    Category as CategoryIcon,
    BarChart as BarChartIcon,
    Dashboard as DashboardIcon,
    Payment as PaymentIcon,
    Group as GroupIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: 8,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    maxWidth: '400px',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: alpha(theme.palette.common.white, 0.7),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
    },
}));

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
    color: 'white',
    backgroundColor: active ? alpha(theme.palette.common.white, 0.2) : 'transparent',
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
}));

const navItems = [
    { path: '/search', label: 'Search', icon: <SearchIcon /> },
    { path: '/leads', label: 'Leads', icon: <PeopleIcon /> },
    { path: '/businesses', label: 'Businesses', icon: <BusinessIcon /> },
    { path: '/clients', label: 'Clients', icon: <GroupIcon /> },
    { path: '/history', label: 'History', icon: <HistoryIcon /> },
    { path: '/catalog', label: 'Catalog', icon: <CategoryIcon /> },
    { path: '/crm', label: 'CRM', icon: <DashboardIcon /> },
    { path: '/payments', label: 'Payments', icon: <PaymentIcon /> },
    { path: '/analytics', label: 'Analytics', icon: <BarChartIcon /> },
];

const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const auth = getAuth();

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    return (
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar sx={{ minHeight: 64 }}>
                {/* Logo */}
                <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                    onClick={() => navigate('/')}
                >
                    <img src="/favicon.png" alt="Logo" style={{ height: 32 }} />
                    Site Reviver
                </Typography>

                {/* Navigation Items */}
                <Box sx={{ display: 'flex', flexGrow: 1, gap: 1, ml: 2 }}>
                    {navItems.map((item) => (
                        <NavButton
                            key={item.path}
                            active={location.pathname === item.path}
                            onClick={() => navigate(item.path)}
                            startIcon={item.icon}
                        >
                            {item.label}
                        </NavButton>
                    ))}
                </Box>

                {/* Profile Menu */}
                <IconButton
                    onClick={handleProfileMenuOpen}
                    size="small"
                    sx={{ ml: 1 }}
                >
                    <Avatar
                        alt="User Avatar"
                        src="/avatar.jpg"
                        sx={{ width: 32, height: 32 }}
                    />
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <MenuItem onClick={() => navigate('/account')}>Account Settings</MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar; 